import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useState } from 'react';
import { authApi } from '../auth/api';
import { PrototypeIcon } from './PrototypeIcon';
import { ConnectorLogoSettings } from './ConnectorLogoSettings';
import {
  platformApi,
  type PlatformAdministratorInvitationStatus,
  type PlatformAdministratorResult,
} from './platform-api';

export type SupervisorSection = 'administrators' | 'settings';

interface SupervisorAdministrationProps {
  readonly section: SupervisorSection;
  readonly csrfToken: string;
  readonly onSessionsPurged: (revokedSessions: number) => void;
}

export function SupervisorAdministration({
  section,
  onSessionsPurged,
}: SupervisorAdministrationProps): React.JSX.Element {
  return section === 'administrators' ? (
    <AdministratorsPanel />
  ) : (
    <PlatformSettingsPanel onSessionsPurged={onSessionsPurged} />
  );
}

function AdministratorsPanel(): React.JSX.Element {
  const [administrators, setAdministrators] = useState<readonly PlatformAdministratorResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [issuingMembershipId, setIssuingMembershipId] = useState<string | null>(null);
  const [detachingAdministrator, setDetachingAdministrator] =
    useState<PlatformAdministratorResult | null>(null);
  const [detachReason, setDetachReason] = useState('');
  const [detaching, setDetaching] = useState(false);
  const [detachError, setDetachError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeFailed, setNoticeFailed] = useState(false);

  async function loadAdministrators(cancelled?: () => boolean): Promise<void> {
    setLoadError(false);
    try {
      const result = await platformApi.listAdministrators();
      if (!cancelled?.()) setAdministrators(result);
    } catch {
      if (!cancelled?.()) setLoadError(true);
    } finally {
      if (!cancelled?.()) setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadAdministrators(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, []);

  async function reissueInvitation(administrator: PlatformAdministratorResult): Promise<void> {
    if (issuingMembershipId !== null || detaching) return;
    setIssuingMembershipId(administrator.membershipId);
    setNotice(null);
    setNoticeFailed(false);
    try {
      const { csrfToken } = await authApi.csrfToken();
      await platformApi.reissueAdministratorInvitation(administrator.membershipId, csrfToken);
      await loadAdministrators();
      setNotice(
        `L’invitation de ${administrator.givenName} ${administrator.familyName} a été réémise.`,
      );
    } catch {
      setNoticeFailed(true);
      setNotice('L’invitation n’a pas pu être réémise. Vérifiez la session et réessayez.');
    } finally {
      setIssuingMembershipId(null);
    }
  }

  function requestDetach(administrator: PlatformAdministratorResult): void {
    if (issuingMembershipId !== null || detaching) return;
    setDetachReason('');
    setDetachError(null);
    setNotice(null);
    setDetachingAdministrator(administrator);
  }

  async function confirmDetach(): Promise<void> {
    if (detachingAdministrator === null || detaching || detachReason.trim().length === 0) return;
    setDetaching(true);
    setDetachError(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      await platformApi.detachAdministrator(
        detachingAdministrator.membershipId,
        detachReason,
        csrfToken,
      );
      const detachedName = `${detachingAdministrator.givenName} ${detachingAdministrator.familyName}`;
      const organizationName = detachingAdministrator.organization.displayName;
      setDetachingAdministrator(null);
      setDetachReason('');
      setNoticeFailed(false);
      await loadAdministrators();
      setNotice(
        `${detachedName} a été détaché de ${organizationName}. Son compte Tosho est conservé.`,
      );
    } catch {
      setDetachError(
        'Le détachement n’a pas pu être exécuté. Actualisez les données puis réessayez.',
      );
    } finally {
      setDetaching(false);
    }
  }

  return (
    <div className="app-content supervisor-administration">
      <div className="dashboard-heading">
        <div>
          <p className="dashboard-heading__eyebrow">Supervision de plateforme</p>
          <h1>Administrateurs</h1>
          <p className="dashboard-heading__intro">
            Gérez les administrateurs d’organisation sans accéder aux projets, clients, finances ou
            autres données métier de leurs organisations.
          </p>
        </div>
      </div>

      <section className="supervisor-panel" aria-labelledby="supervisor-administrators-title">
        <header className="supervisor-panel__header">
          <div>
            <p className="dashboard-panel__eyebrow">Accès organisations</p>
            <h2 id="supervisor-administrators-title">Administrateurs d’organisation</h2>
          </div>
          <span
            className="supervisor-panel__count"
            aria-label={`${administrators.length} administrateurs`}
          >
            {administrators.length}
          </span>
        </header>

        {notice ? (
          <p
            className={
              'supervisor-notice tosho-feedback ' + (noticeFailed ? 'is-error' : 'is-success')
            }
            role="status"
          >
            {notice}
          </p>
        ) : null}

        {loading ? <SupervisorLoadingState /> : null}
        {!loading && loadError ? (
          <SupervisorErrorState onRetry={() => void loadAdministrators()} />
        ) : null}
        {!loading && !loadError && administrators.length === 0 ? (
          <div className="dashboard-empty-state dashboard-empty-state--compact">
            <span className="dashboard-empty-state__mark" aria-hidden="true">
              <PrototypeIcon name="users-round" />
            </span>
            <strong>Aucun administrateur d’organisation</strong>
            <p>Aucun rattachement administrateur courant n’est actuellement enregistré.</p>
          </div>
        ) : null}
        {!loading && !loadError && administrators.length > 0 ? (
          <div className="supervisor-table-scroll">
            <table className="supervisor-table">
              <thead>
                <tr>
                  <th scope="col">Organisation</th>
                  <th scope="col">Administrateur</th>
                  <th scope="col">Compte</th>
                  <th scope="col">Invitation</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {administrators.map((administrator) => (
                  <AdministratorRow
                    key={administrator.membershipId}
                    administrator={administrator}
                    issuing={issuingMembershipId === administrator.membershipId}
                    actionDisabled={issuingMembershipId !== null || detaching}
                    onReissue={() => void reissueInvitation(administrator)}
                    onDetach={() => requestDetach(administrator)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {detachingAdministrator ? (
        <ModalBackdrop
          className="tosho-modal-backdrop supervisor-confirmation-backdrop"
          aria-labelledby="supervisor-detach-administrator-title"
          close={
            <ModalCloseButton
              disabled={detaching}
              onClick={() => setDetachingAdministrator(null)}
            />
          }
        >
          <section className="tosho-modal-panel supervisor-confirmation-dialog">
            <header className="tosho-modal-header">
              <span className="supervisor-confirmation-dialog__icon" aria-hidden="true">
                <PrototypeIcon name="users-round" />
              </span>
              <div>
                <p>Rattachement d’organisation</p>
                <h2 id="supervisor-detach-administrator-title">Détacher cet Administrateur ?</h2>
              </div>
            </header>
            <p>
              <strong>
                {detachingAdministrator.givenName} {detachingAdministrator.familyName}
              </strong>{' '}
              perdra immédiatement l’accès à {detachingAdministrator.organization.displayName}. Son
              compte Tosho et l’historique du rattachement sont conservés. L’organisation peut
              rester temporairement sans Administrateur actif.
            </p>
            <label className="supervisor-confirmation-dialog__reason">
              <span>Motif du détachement</span>
              <textarea
                value={detachReason}
                maxLength={500}
                rows={4}
                disabled={detaching}
                onChange={(event) => setDetachReason(event.currentTarget.value)}
              />
              <small>{detachReason.trim().length}/500 caractères</small>
            </label>
            {detachError ? (
              <p className="supervisor-danger-error tosho-feedback is-error" role="alert">
                {detachError}
              </p>
            ) : null}
            <div className="supervisor-confirmation-dialog__actions">
              <button
                className="supervisor-button supervisor-button--secondary"
                type="button"
                disabled={detaching}
                onClick={() => setDetachingAdministrator(null)}
              >
                Annuler
              </button>
              <button
                className="supervisor-button supervisor-button--danger"
                type="button"
                disabled={detaching || detachReason.trim().length === 0}
                onClick={() => void confirmDetach()}
              >
                {detaching ? 'Détachement…' : 'Détacher l’Administrateur'}
              </button>
            </div>
          </section>
        </ModalBackdrop>
      ) : null}
    </div>
  );
}

function AdministratorRow({
  administrator,
  issuing,
  actionDisabled,
  onReissue,
  onDetach,
}: {
  readonly administrator: PlatformAdministratorResult;
  readonly issuing: boolean;
  readonly actionDisabled: boolean;
  readonly onReissue: () => void;
  readonly onDetach: () => void;
}): React.JSX.Element {
  const canReissue =
    administrator.accountStatus === 'PENDING_INVITATION' &&
    administrator.membershipStatus === 'PENDING_INVITATION';

  return (
    <tr>
      <td>
        <strong>{administrator.organization.displayName}</strong>
        <small>{statusLabel(administrator.organization.status)}</small>
      </td>
      <td>
        <strong>
          {administrator.givenName} {administrator.familyName}
        </strong>
        <small>{administrator.email}</small>
      </td>
      <td>
        <StatusPill status={administrator.accountStatus} />
      </td>
      <td>
        {administrator.latestInvitation ? (
          <span className="supervisor-invitation-state">
            <StatusPill status={administrator.latestInvitation.status} />
            <small>
              {invitationDateLabel(
                administrator.latestInvitation.status,
                administrator.latestInvitation.expiresAt,
              )}
            </small>
          </span>
        ) : (
          <span className="supervisor-muted">Aucune invitation</span>
        )}
      </td>
      <td className="supervisor-table__actions">
        {canReissue ? (
          <button
            className="supervisor-button supervisor-button--secondary"
            type="button"
            disabled={actionDisabled}
            onClick={onReissue}
          >
            {issuing ? 'Envoi…' : 'Renvoyer l’invitation'}
          </button>
        ) : null}
        <button
          className="supervisor-button supervisor-button--danger"
          type="button"
          disabled={actionDisabled}
          onClick={onDetach}
        >
          Détacher
        </button>
      </td>
    </tr>
  );
}

function StatusPill({ status }: { readonly status: string }): React.JSX.Element {
  const normalized = status.toLowerCase().replaceAll('_', '-');
  return (
    <span className={`supervisor-status supervisor-status--${normalized}`}>
      {statusLabel(status)}
    </span>
  );
}

function statusLabel(status: string): string {
  switch (status) {
    case 'PENDING_INVITATION':
      return 'Invitation en attente';
    case 'PENDING':
      return 'En attente';
    case 'ACTIVE':
      return 'Actif';
    case 'SUSPENDED':
      return 'Suspendu';
    case 'ARCHIVED':
      return 'Archivé';
    case 'DRAFT':
      return 'Brouillon';
    case 'EXPIRED':
      return 'Expirée';
    case 'CONSUMED':
      return 'Utilisée';
    case 'REVOKED':
      return 'Révoquée';
    default:
      return status;
  }
}

function invitationDateLabel(
  status: PlatformAdministratorInvitationStatus,
  expiresAt: string,
): string {
  const formatted = new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(expiresAt));

  return status === 'PENDING' ? `Expire le ${formatted}` : `Échéance : ${formatted}`;
}

function SupervisorLoadingState(): React.JSX.Element {
  return (
    <div className="supervisor-state" role="status">
      <span className="supervisor-state__spinner" aria-hidden="true" />
      <strong>Chargement des administrateurs…</strong>
    </div>
  );
}

function SupervisorErrorState({ onRetry }: { readonly onRetry: () => void }): React.JSX.Element {
  return (
    <div className="supervisor-state tosho-feedback is-error" role="alert">
      <strong>Impossible de charger les administrateurs.</strong>
      <p>La session ou le service de supervision n’est pas disponible.</p>
      <button
        className="supervisor-button supervisor-button--secondary"
        type="button"
        onClick={onRetry}
      >
        Réessayer
      </button>
    </div>
  );
}

function PlatformSettingsPanel({
  onSessionsPurged,
}: {
  readonly onSessionsPurged: (revokedSessions: number) => void;
}): React.JSX.Element {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function purgeSessions(): Promise<void> {
    if (purging) return;
    setPurging(true);
    setError(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      const result = await platformApi.purgeAllSessions(csrfToken);
      onSessionsPurged(result.revokedSessions);
    } catch {
      setError(
        'La purge globale n’a pas pu être exécutée. Aucune confirmation de révocation n’a été reçue.',
      );
      setPurging(false);
    }
  }

  return (
    <div className="app-content supervisor-administration">
      <div className="dashboard-heading">
        <div>
          <p className="dashboard-heading__eyebrow">Supervision de plateforme</p>
          <h1>Configuration</h1>
          <p className="dashboard-heading__intro">
            Les actions de sécurité globales restent réservées au Superviseur et sont contrôlées
            côté serveur.
          </p>
        </div>
      </div>

      <ConnectorLogoSettings />

      <section
        className="supervisor-panel supervisor-panel--danger"
        aria-labelledby="platform-security-title"
      >
        <header className="supervisor-panel__header">
          <div>
            <p className="dashboard-panel__eyebrow">Sécurité</p>
            <h2 id="platform-security-title">Sessions de la plateforme</h2>
          </div>
          <PrototypeIcon name="shield-check" />
        </header>
        <div className="supervisor-danger-zone">
          <div>
            <strong>Déconnecter tous les utilisateurs</strong>
            <p>
              Révoque immédiatement toutes les sessions actives de Tosho, y compris votre session de
              Superviseur. Tous les utilisateurs devront se reconnecter.
            </p>
          </div>
          <button
            className="supervisor-button supervisor-button--danger"
            type="button"
            onClick={() => {
              setError(null);
              setConfirmationOpen(true);
            }}
          >
            Déconnecter tout le monde
          </button>
        </div>
        {error ? (
          <p className="supervisor-danger-error tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
      </section>

      {confirmationOpen ? (
        <ModalBackdrop
          className="tosho-modal-backdrop supervisor-confirmation-backdrop"
          aria-labelledby="supervisor-purge-title"
          close={<ModalCloseButton disabled={purging} onClick={() => setConfirmationOpen(false)} />}
        >
          <section className="tosho-modal-panel supervisor-confirmation-dialog">
            <header className="tosho-modal-header">
              <span className="supervisor-confirmation-dialog__icon" aria-hidden="true">
                <PrototypeIcon name="shield-check" />
              </span>
              <div>
                <p>Sécurité globale</p>
                <h2 id="supervisor-purge-title">Confirmer la déconnexion générale</h2>
              </div>
            </header>
            <p>
              Cette action révoque toutes les sessions actives, sans exception, puis vous ramène à
              l’écran de connexion. Elle n’efface aucun compte ni aucune donnée métier.
            </p>
            <div className="supervisor-confirmation-dialog__actions">
              <button
                className="supervisor-button supervisor-button--secondary"
                type="button"
                disabled={purging}
                onClick={() => setConfirmationOpen(false)}
              >
                Annuler
              </button>
              <button
                className="supervisor-button supervisor-button--danger"
                type="button"
                disabled={purging}
                onClick={() => void purgeSessions()}
              >
                {purging ? 'Déconnexion…' : 'Révoquer toutes les sessions'}
              </button>
            </div>
          </section>
        </ModalBackdrop>
      ) : null}
    </div>
  );
}
